import { useState } from 'react';
import api from '../../services/api';
import Button from './Button';
import toast from 'react-hot-toast';

const INP = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none';

export default function ProfileCompletionModal({ userData, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: userData.first_name || '',
    last_name:  userData.last_name  || '',
    email:      userData.email      || '',
    password:   '',
    confirm:    '',
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const sf = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const EyeIcon = ({ visible }) => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {visible ? (
        <>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </>
      ) : (
        <>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
        </>
      )}
    </svg>
  );

  const missing = [
    !userData.first_name  && 'first name',
    !userData.last_name   && 'last name',
    !userData.email       && 'email',
    !userData.has_password && 'password',
  ].filter(Boolean);

  async function handleSave() {
    if (!form.first_name.trim()) return toast.error('First name is required');
    if (!form.last_name.trim())  return toast.error('Last name is required');
    if (form.password && form.password.length < 8) return toast.error('Password must be at least 8 characters');
    if (form.password && form.password !== form.confirm) return toast.error('Passwords do not match');
    setSaving(true);
    try {
      await api.patch('/auth/profile', {
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim(),
        ...(form.email.trim() ? { email:    form.email.trim() } : {}),
        ...(form.password     ? { password: form.password }     : {}),
      });
      toast.success('Profile updated successfully');
      onSaved();
    } catch {}
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Complete Your Profile</h2>
            <p className="text-xs text-gray-500 mt-0.5">Missing: {missing.join(', ')}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors p-1 mt-0.5 flex-shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <p className="text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
            Please fill in the details below to complete your account setup.
          </p>

          {/* Name row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.first_name}
                onChange={sf('first_name')}
                className={INP}
                placeholder="First name"
                maxLength={100}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.last_name}
                onChange={sf('last_name')}
                className={INP}
                placeholder="Last name"
                maxLength={100}
              />
            </div>
          </div>

          {/* Email */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Email Address</label>
            <input
              type="email"
              value={form.email}
              onChange={sf('email')}
              className={INP}
              placeholder="yourname@example.com"
            />
          </div>

          {/* Password */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              {userData.has_password ? 'New Password' : 'Password'}{' '}
              <span className="text-xs text-gray-400 font-normal">(optional)</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={sf('password')}
                className={INP + ' pr-10'}
                placeholder="Min 8 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                <EyeIcon visible={showPassword} />
              </button>
            </div>
          </div>

          {/* Confirm password (shown only when entering a new password) */}
          {form.password && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={form.confirm}
                  onChange={sf('confirm')}
                  className={INP + ' pr-10'}
                  placeholder="Repeat password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  <EyeIcon visible={showConfirm} />
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <Button variant="secondary" onClick={onClose}>Later</Button>
            <Button fullWidth loading={saving} onClick={handleSave}>Save</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
